import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { appointmentsAPI, servicesAPI, clientsAPI, staffAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, Plus, MoreVertical, Check, X, Calendar as CalendarIcon, Clock, UserX } from "lucide-react";
import { format, addDays, startOfWeek, endOfWeek, addWeeks, subWeeks } from "date-fns";
import { toast } from "sonner";
import { formatTime, getStatusColor, cn } from "@/lib/utils";
import NewAppointmentDialog from "@/components/NewAppointmentDialog";

export default function CalendarPage() {
    const navigate = useNavigate();
    const [view, setView] = useState("week");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewAppt, setShowNewAppt] = useState(false);
    const [services, setServices] = useState([]);
    const [clients, setClients] = useState([]);
    const [staff, setStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState("all");
    const [prefilledSlot, setPrefilledSlot] = useState(null);
    
    const [actionDialog, setActionDialog] = useState({
        open: false,
        appointment: null,
        action: null,
        title: '',
        description: '',
    });

    useEffect(() => {
        loadData();
    }, [currentDate, view, selectedStaff]);

    const loadData = async () => {
        setLoading(true);
        try {
            const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
            
            const params = view === "week" 
                ? { start_date: format(weekStart, 'yyyy-MM-dd'), end_date: format(weekEnd, 'yyyy-MM-dd') }
                : { date: format(currentDate, 'yyyy-MM-dd') };
            
            const [apptsRes, servicesRes, clientsRes, staffRes] = await Promise.all([
                appointmentsAPI.getAll(params),
                servicesAPI.getAll(),
                clientsAPI.getAll(),
                staffAPI.getAll()
            ]);
            
            const extractData = (response, key) => {
                const data = response.data;
                if (Array.isArray(data)) return data;
                if (data?.[key] && Array.isArray(data[key])) return data[key];
                if (data?.data && Array.isArray(data.data)) return data.data;
                if (data?.results && Array.isArray(data.results)) return data.results;
                return [];
            };
            
            let apptsList = extractData(apptsRes, 'appointments');
            const servicesList = extractData(servicesRes, 'services');
            const clientsList = extractData(clientsRes, 'clients');
            const staffList = extractData(staffRes, 'staff');
            
            if (selectedStaff !== "all") {
                apptsList = apptsList.filter(a => a.staff_id === selectedStaff);
            }
            
            setAppointments(apptsList);
            setServices(servicesList);
            setClients(clientsList);
            setStaff(staffList);
        } catch (error) {
            toast.error("Failed to load calendar");
        } finally {
            setLoading(false);
        }
    };

    const navigateWeek = (direction) => {
        setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    };

    const getWeekDays = () => {
        const start = startOfWeek(currentDate, { weekStartsOn: 1 });
        return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    };

    const getAppointmentsForDate = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return appointments.filter(a => a.date === dateStr);
    };

    const handleAppointmentClick = (appointmentId) => {
        navigate(`/appointments/${appointmentId}`);
    };

    const handleTimeSlotClick = (date, time) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        setPrefilledSlot({
            date: dateStr,
            time: `${time}:00`
        });
        setShowNewAppt(true);
    };

    const confirmAction = (appointment, action) => {
        const actionConfig = {
            accept: {
                title: 'Confirm Appointment',
                description: `Mark this appointment as confirmed?`,
            },
            complete: {
                title: 'Complete Appointment',
                description: `Mark this appointment as completed?`,
            },
            cancel: {
                title: 'Cancel Appointment',
                description: `Are you sure you want to cancel this appointment? This action cannot be undone.`,
            },
            'no-show': {
                title: 'Mark as No Show',
                description: `Mark this appointment as no-show?`,
            },
        };

        setActionDialog({
            open: true,
            appointment,
            action,
            ...actionConfig[action],
        });
    };

    const handleAppointmentAction = async () => {
        const { appointment, action } = actionDialog;
        
        try {
            let status;
            let message;
            
            switch(action) {
                case 'accept':
                    status = 'confirmed';
                    message = 'Appointment confirmed';
                    break;
                case 'complete':
                    status = 'completed';
                    message = 'Appointment completed';
                    break;
                case 'cancel':
                    status = 'cancelled';
                    message = 'Appointment cancelled';
                    break;
                case 'no-show':
                    status = 'no_show';
                    message = 'Marked as no-show';
                    break;
                default:
                    return;
            }
            
            await appointmentsAPI.update(appointment.id, { status });
            toast.success(message);
            setActionDialog({ open: false, appointment: null, action: null, title: '', description: '' });
            loadData();
        } catch (error) {
            toast.error("Failed to update appointment");
        }
    };

    const handleReschedule = (appointmentId) => {
        navigate(`/appointments/${appointmentId}?action=reschedule`);
    };

    const timeSlots = [];
    for (let h = 8; h <= 20; h++) {
        timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    }

    const weekDays = getWeekDays();

    const getStatusBadgeWithIcon = (status) => {
        const icons = {
            confirmed: <Check className="w-3 h-3" />,
            completed: <Check className="w-3 h-3" />,
            cancelled: <X className="w-3 h-3" />,
            no_show: <UserX className="w-3 h-3" />,
        };

        return (
            <Badge className={cn("text-[10px] h-5 gap-1", getStatusColor(status))}>
                {icons[status]}
                {status?.replace('_', ' ')}
            </Badge>
        );
    };

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Calendar</h1>
                    <p className="text-muted-foreground">
                        {format(currentDate, 'MMMM yyyy')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {staff.length > 0 && (
                        <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="All Staff" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Staff</SelectItem>
                                {staff.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <Button onClick={() => setShowNewAppt(true)} className="rounded-full">
                        <Plus className="w-4 h-4 mr-2" />
                        New Appointment
                    </Button>
                </div>
            </div>

            {/* Week Navigation */}
            <div className="flex items-center justify-between">
                <Button variant="outline" size="icon" onClick={() => navigateWeek('prev')}>
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2">
                    <Button 
                        variant={view === 'day' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => setView('day')}
                    >
                        Day
                    </Button>
                    <Button 
                        variant={view === 'week' ? 'default' : 'ghost'} 
                        size="sm"
                        onClick={() => setView('week')}
                    >
                        Week
                    </Button>
                </div>
                <Button variant="outline" size="icon" onClick={() => navigateWeek('next')}>
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>

            {/* Calendar Grid */}
            {loading ? (
                <div className="flex items-center justify-center h-[500px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : view === 'week' ? (
                <Card className="border-border/40 shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="min-w-[800px]">
                            {/* Days Header */}
                            <div className="grid grid-cols-8 border-b bg-secondary/20">
                                <div className="p-3 text-sm font-medium text-muted-foreground border-r">Time</div>
                                {weekDays.map((day, i) => {
                                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                                    const dayAppointments = getAppointmentsForDate(day);
                                    const appointmentCount = dayAppointments.length;
                                    
                                    return (
                                        <div 
                                            key={i} 
                                            className={cn(
                                                "p-3 text-center border-r last:border-r-0",
                                                isToday && "bg-accent"
                                            )}
                                        >
                                            <p className="text-xs text-muted-foreground uppercase">{format(day, 'EEE')}</p>
                                            <p className={cn(
                                                "text-lg font-semibold",
                                                isToday && "text-primary"
                                            )}>{format(day, 'd')}</p>
                                            {appointmentCount > 0 && (
                                                <Badge variant="outline" className="mt-1 text-[10px]">
                                                    {appointmentCount} {appointmentCount === 1 ? 'appt' : 'appts'}
                                                </Badge>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Time Slots */}
                            <div className="max-h-[600px] overflow-y-auto">
                                {timeSlots.map((time) => (
                                    <div key={time} className="grid grid-cols-8 border-b last:border-b-0 hover:bg-secondary/5 transition-colors">
                                        <div className="p-2 text-xs text-muted-foreground border-r flex items-start justify-center font-medium">
                                            {formatTime(`${time}:00`)}
                                        </div>
                                        {weekDays.map((day, i) => {
                                            const dayAppts = getAppointmentsForDate(day).filter(a => a.time?.startsWith(time.split(':')[0]));
                                            const hasAppointments = dayAppts.length > 0;
                                            const isPast = new Date(`${format(day, 'yyyy-MM-dd')}T${time}`) < new Date();
                                            
                                            return (
                                                <div 
                                                    key={i} 
                                                    className={cn(
                                                        "p-1 border-r last:border-r-0 min-h-[70px] relative",
                                                        !hasAppointments && !isPast && "cursor-pointer hover:bg-primary/5",
                                                        isPast && "bg-secondary/30"
                                                    )}
                                                    onClick={() => !hasAppointments && !isPast && handleTimeSlotClick(day, time)}
                                                    title={!hasAppointments && !isPast ? "Click to book appointment" : ""}
                                                >
                                                    {!hasAppointments && !isPast && (
                                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                                            <Plus className="w-4 h-4 text-primary" />
                                                        </div>
                                                    )}
                                                    
                                                    {dayAppts.map((appt) => (
                                                        <div
                                                            key={appt.id}
                                                            className="group relative w-full p-2 rounded-lg bg-primary/10 border border-primary/20 text-xs mb-1 hover:bg-primary/20 transition-all hover:shadow-sm"
                                                        >
                                                            <div className="flex items-start justify-between gap-1">
                                                                <div 
                                                                    className="flex-1 cursor-pointer min-w-0"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAppointmentClick(appt.id);
                                                                    }}
                                                                >
                                                                    <p className="font-medium truncate text-foreground">{appt.client_name}</p>
                                                                    <p className="text-muted-foreground truncate text-[10px]">{appt.service_name}</p>
                                                                    <div className="mt-1">
                                                                        {getStatusBadgeWithIcon(appt.status)}
                                                                    </div>
                                                                </div>
                                                                
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="icon" 
                                                                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                                                        >
                                                                            <MoreVertical className="h-3 w-3" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                                                        {appt.status === 'scheduled' && (
                                                                            <>
                                                                                <DropdownMenuItem onClick={() => confirmAction(appt, 'accept')}>
                                                                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                                                                    Confirm
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuItem onClick={() => confirmAction(appt, 'complete')}>
                                                                                    <Check className="w-4 h-4 mr-2 text-blue-600" />
                                                                                    Complete
                                                                                </DropdownMenuItem>
                                                                            </>
                                                                        )}
                                                                        {appt.status === 'confirmed' && (
                                                                            <DropdownMenuItem onClick={() => confirmAction(appt, 'complete')}>
                                                                                <Check className="w-4 h-4 mr-2 text-blue-600" />
                                                                                Complete
                                                                            </DropdownMenuItem>
                                                                        )}
                                                                        {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                                                                            <>
                                                                                <DropdownMenuSeparator />
                                                                                <DropdownMenuItem onClick={() => handleReschedule(appt.id)}>
                                                                                    <CalendarIcon className="w-4 h-4 mr-2 text-blue-600" />
                                                                                    Reschedule
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuItem onClick={() => confirmAction(appt, 'cancel')}>
                                                                                    <X className="w-4 h-4 mr-2 text-red-600" />
                                                                                    Cancel
                                                                                </DropdownMenuItem>
                                                                            </>
                                                                        )}
                                                                        {appt.status === 'confirmed' && (
                                                                            <DropdownMenuItem onClick={() => confirmAction(appt, 'no-show')}>
                                                                                <UserX className="w-4 h-4 mr-2 text-yellow-600" />
                                                                                No Show
                                                                            </DropdownMenuItem>
                                                                        )}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            ) : (
                /* Day View */
                <div className="grid lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-1 border-border/40 shadow-card">
                        <CardContent className="p-4">
                            <Calendar
                                mode="single"
                                selected={currentDate}
                                onSelect={(d) => d && setCurrentDate(d)}
                                className="w-full"
                            />
                        </CardContent>
                    </Card>

                    <Card className="lg:col-span-2 border-border/40 shadow-card">
                        <CardHeader>
                            <CardTitle className="font-heading">
                                {format(currentDate, 'EEEE, MMMM d, yyyy')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {getAppointmentsForDate(currentDate).length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>No appointments for this day</p>
                                    <Button 
                                        variant="link" 
                                        onClick={() => setShowNewAppt(true)}
                                        className="mt-2"
                                    >
                                        Book an appointment
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {getAppointmentsForDate(currentDate).map((appt) => (
                                        <div
                                            key={appt.id}
                                            className="group flex items-center gap-4 p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                                        >
                                            <div 
                                                className="flex-1 flex items-center gap-4 cursor-pointer"
                                                onClick={() => handleAppointmentClick(appt.id)}
                                            >
                                                <div className="flex-shrink-0 w-20 text-center">
                                                    <Clock className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                                                    <p className="text-lg font-semibold">{formatTime(appt.time)}</p>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium">{appt.client_name}</p>
                                                    <p className="text-sm text-muted-foreground">{appt.service_name}</p>
                                                    {appt.staff_name && (
                                                        <p className="text-xs text-muted-foreground">with {appt.staff_name}</p>
                                                    )}
                                                </div>
                                                {getStatusBadgeWithIcon(appt.status)}
                                            </div>
                                            
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {appt.status === 'scheduled' && (
                                                        <>
                                                            <DropdownMenuItem onClick={() => confirmAction(appt, 'accept')}>
                                                                <Check className="w-4 h-4 mr-2 text-green-600" />
                                                                Confirm
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => confirmAction(appt, 'complete')}>
                                                                <Check className="w-4 h-4 mr-2 text-blue-600" />
                                                                Complete
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    {appt.status === 'confirmed' && (
                                                        <DropdownMenuItem onClick={() => confirmAction(appt, 'complete')}>
                                                            <Check className="w-4 h-4 mr-2 text-blue-600" />
                                                            Complete
                                                        </DropdownMenuItem>
                                                    )}
                                                    {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                                                        <>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem onClick={() => handleReschedule(appt.id)}>
                                                                <CalendarIcon className="w-4 h-4 mr-2 text-blue-600" />
                                                                Reschedule
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => confirmAction(appt, 'cancel')}>
                                                                <X className="w-4 h-4 mr-2 text-red-600" />
                                                                Cancel
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    {appt.status === 'confirmed' && (
                                                        <DropdownMenuItem onClick={() => confirmAction(appt, 'no-show')}>
                                                            <UserX className="w-4 h-4 mr-2 text-yellow-600" />
                                                            No Show
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* New Appointment Dialog */}
            <NewAppointmentDialog 
                open={showNewAppt}
                onOpenChange={(open) => {
                    setShowNewAppt(open);
                    if (!open) setPrefilledSlot(null);
                }}
                clients={clients}
                services={services}
                staff={staff}
                onSuccess={loadData}
                prefilledData={prefilledSlot}
            />

            {/* Action Confirmation Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, appointment: null, action: null, title: '', description: '' })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{actionDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {actionDialog.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleAppointmentAction}>
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}